import { gql } from '@apollo/client';

import { ResponsiveImageFragment } from 'approot/shared/graphql/responsive-images/responsive-images.graphql';
import { interactiveContentStyleBackgroundFragment } from './styles/styles.graphql';
import { DictionaryBookItemFragment } from 'approot/dictionary/dictionary.graphql';
import { completionFragment } from 'approot/planner/manage/planner.graphql';
import { SuggestedAnswerFragment } from './components/suggested-answers/suggested-answers.graphql';
import { AnswerFragment } from './present/activity-answers.graphql';
import { ActivityOutcomesFragment } from './components/teaching-notes/key-activity-information/activity-outcomes.graphql';
import { guidedReadThroughFragment } from 'approot/shared/stimulus/stimulus.graphql';

const InteractiveSubActivityFragment = gql`
  fragment InteractiveSubActivityFragment on SubActivityComponentProperties {
    activityComponentId
    subActivitySort
    subActivityLabel
    instructions
    areInstructionsVisible
    previews {
      format
      url
    }
  }
`;

const ComponentOuterStyleFragment = gql`
  fragment ComponentOuterStyleFragment on ComponentStyle {
    outerBorder
    outerBaseColour
    outerBackgroundColour
  }
`;

const InteractiveInfoBoxFragment = gql`
  fragment InteractiveInfoBoxFragment on ActivitiesInfoBoxComponent {
    id
    language
    version
    type
    properties {
      richtext
      style {
        arrowPosition
        arrowAlignment
        baseColour
        backgroundColour
        border
        width
        textAlignment
        ...ComponentOuterStyleFragment
      }
    }
    parentProperties {
      columnId
    }
    sort
    sectionId
  }
  ${ComponentOuterStyleFragment}
`;

export const InteractiveResponseFragment = gql`
  fragment InteractiveResponseFragment on ActivitiesResponseComponent {
    id
    type
    language
    version
    properties {
      textResponse {
        fields {
          itemId
          placeholder
          numberOfLines
          sort
          isPlaceholderScaffold
          baseColour
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
    sort
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveVideoFragment = gql`
  fragment InteractiveVideoFragment on ActivitiesVideoComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      coverImage {
        original
      }
      attributions {
        author
        copyright
        licence
        modifications
        modificationDescription
        contentWarning
      }
      videoData {
        link
        embed
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      link
      resourceName
      description
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

const InteractiveAudioFragment = gql`
  fragment InteractiveAudioFragment on ActivitiesAudioComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      coverImage {
        original
      }
      attributions {
        author
        copyright
        licence
        modifications
        modificationDescription
        contentWarning
      }
      videoData {
        link
        embed
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      link
      resourceName
      description
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

const InteractiveImageFragment = gql`
  fragment InteractiveImageFragment on ActivitiesImageComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      caption
      altText
      sourceUrl
      dimensionData {
        width
        height
      }
      cropData {
        x
        y
        width
        height
        zoom
        naturalWidth
        naturalHeight
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      image {
        original
        fileName
        key
        data {
          designId
          flip
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      image {
        key
        fileName
        original
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
      link
      imageResourceName: resourceName
      imageDescription: description
      altText
      coverImage {
        key
        fileName
        original
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveImageSphereFragment = gql`
  fragment InteractiveImageSphereFragment on ActivitiesImageSphereComponent {
    id
    type
    language
    version
    properties {
      showCaptionOnLesson
      caption
      altText
      sourceUrl
      dimensionData {
        width
        height
      }
      image {
        original
        fileName
        key
        data {
          flip
        }
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      printImage {
        original
        fileName
        key
        alt
      }
      printImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

const InteractiveLinkFragment = gql`
  fragment InteractiveLinkFragment on ActivitiesLinkComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      layout {
        display
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      link
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveWebToolFragment = gql`
  fragment InteractiveWebToolFragment on ActivitiesWebToolComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      layout {
        display
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      link
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveGuidedResearchFragment = gql`
  fragment InteractiveGuidedResearchFragment on ActivitiesGuidedResearchComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      layout {
        display
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      links {
        link
        title
        description
      }
      link
      slug
      coverImage {
        ...ResponsiveImageFragment
      }
      resourceName
      description
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveSlideshowFragment = gql`
  fragment InteractiveSlideshowFragment on ActivitiesSlideshowComponent {
    id
    type
    language
    version
    sort
    slideshowImages {
      ...InteractiveImageFragment
    }
    properties {
      dimensionData {
        width
        height
      }
      showSlideCaptionOnLesson: showCaptionOnLesson
      style {
        ...ComponentOuterStyleFragment
      }
    }
    sectionId
  }
  ${InteractiveImageFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveDocumentFragment = gql`
  fragment InteractiveDocumentFragment on ActivitiesDocumentComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      resourceName
      description
      link
      fileName
      documentKey
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

export const InteractiveEbookFragment = gql`
  fragment InteractiveEbookFragment on ActivitiesEbookComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      resourceName
      description
      link
      fileName
      documentKey
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

export const InteractiveEbookInteractiveFragment = gql`
  fragment InteractiveEbookInteractiveFragment on ActivitiesInteractiveEbookComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      link
      slug
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      guidedReadThroughs {
        ...GuidedReadThrough
      }
    }
    sectionId
    sort
  }
  ${guidedReadThroughFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveInteractiveFragment = gql`
  fragment InteractiveInteractiveFragment on ActivitiesInteractiveComponent {
    id
    type
    language
    version
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    stimulusResourceProperties {
      link
      slug
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

export const InteractiveMultipleChoiceFragment = gql`
  fragment InteractiveMultipleChoiceFragment on ActivitiesMultipleChoiceComponent {
    id
    type
    language
    version
    properties {
      layout {
        display
      }
      multipleChoice {
        variant
        revealAnswer
        optionMinHeight
        rowsGridLayout
        showOptionLetter
        imagePosition
        answerMode
        isFreeAnswerMultiSelect
        options {
          itemId
          sort
          correctAnswerBoolean
          text
          sourceUrl
          image {
            itemId
            key
            fileName
            alt
            original
            data {
              flip
            }
          }
          imageUrls {
            width
            height
            urls {
              url
              quality
            }
          }
          imageLayout
          cropData {
            x
            y
            width
            height
            zoom
            naturalWidth
            naturalHeight
          }
        }
        moreInformation
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    sort
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveEquationResponseFragment = gql`
  fragment InteractiveEquationResponseFragment on ActivitiesEquationResponseComponent {
    id
    type
    language
    version
    properties {
      revealAnswer
      operation
      layout {
        orientation
      }
      moreInformation
      firstOperand
      secondOperand
      style {
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    sectionId
    sort
  }
  ${InteractiveSubActivityFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveVocabularyFragment = gql`
  fragment InteractiveVocabularyFragment on ActivitiesVocabularyComponent {
    id
    type
    language
    version
    properties {
      definitionIds
      style {
        ...ComponentOuterStyleFragment
      }
    }
    parentProperties {
      columnId
    }
    vocabularyDefinitions {
      ...DictionaryBookItemFragment
    }
    sectionId
    sort
  }
  ${DictionaryBookItemFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveFillInBlanksResponseFragment = gql`
  fragment InteractiveFillInBlanksResponseFragment on ActivitiesFillInBlanksResponseComponent {
    id
    type
    language
    version
    sort
    properties {
      fillInBlanks {
        fontFamily
        fontSize
        sentences {
          itemId
          sort
          textBlocks {
            itemId
            sort
            text
          }
          answerBlocks {
            itemId
            sort
            width
            colour
            showLabel
            label
            showPlaceholderAsScaffold
            placeholder
            punctuation
          }
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveDrawFragment = gql`
  fragment InteractiveDrawFragment on ActivitiesDrawComponent {
    id
    type
    language
    version
    sort
    properties {
      stimulusId
      draw {
        originalHeight
        originalWidth
        data
        updatedAt
      }
      dimensionData {
        width
        height
      }
      image {
        ...ResponsiveImageFragment
        imageType
        classification
        data {
          flip
        }
      }
      altText
      sourceUrl
      style {
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveDiscussionFragment = gql`
  fragment InteractiveDiscussionFragment on ActivitiesDiscussionComponent {
    id
    type
    language
    version
    sort
    properties {
      style {
        arrowPosition
        arrowAlignment
        baseColour
        backgroundColour
        border
        width
        textAlignment
        ...ComponentOuterStyleFragment
      }
      discussion {
        responseTypes
        instruction
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveTableFragment = gql`
  fragment InteractiveTableFragment on ActivitiesTableComponent {
    id
    type
    language
    version
    sort
    properties {
      table {
        isReadOnly
        showColumnHeaders
        showRowHeaders
        columnWidths
        rows {
          cells {
            value
          }
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveInteractiveResponseSequenceFragment = gql`
  fragment InteractiveInteractiveResponseFragment on ActivitiesInteractiveResponseComponent {
    id
    type
    language
    version
    sort
    properties {
      interactiveResponse {
        title
        subTitle
        howToPlay
        moreInformation
        labelLength
        labelBorder
        checkAnswerAtEnd
        answerType
        variant
        items {
          variant
          itemId
          questionLabel
          answerLabel
          sort
          sourceUrl
          imageMode
          text
          richtext
          baseColour
          cropData {
            x
            y
            width
            height
            zoom
            naturalWidth
            naturalHeight
          }
          image {
            itemId
            key
            fileName
            description
            original
            data {
              flip
            }
          }
          audio {
            key
            fileName
            original
          }
          imageUrls {
            width
            height
            urls {
              url
              quality
            }
          }
        }
      }
      responseDimensionData: dimensionData {
        width
        height
      }
      style {
        backgroundImage {
          ...ResponsiveImageFragment
        }
        baseColour
        backgroundMatchActivity
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveInteractiveResponseCategoriseFragment = gql`
  fragment InteractiveInteractiveResponseCategoriseFragment on ActivitiesCategoriseResponseComponent {
    id
    type
    language
    version
    sort
    properties {
      interactiveResponseCategorise {
        title
        subTitle
        howToPlay
        moreInformation
        checkAnswerAtEnd
        showNumberOfItemsHint
        answerType
        variant
        items {
          variant
          itemId
          questionLabel
          answerLabel
          sort
          sourceUrl
          categoryId
          imageMode
          baseColour
          text
          richtext
          cropData {
            x
            y
            width
            height
            zoom
            naturalWidth
            naturalHeight
          }
          image {
            itemId
            key
            fileName
            description
            original
            data {
              flip
            }
          }
          audio {
            key
            fileName
            original
          }
          imageUrls {
            width
            height
            urls {
              url
              quality
            }
          }
        }
        categories {
          id
          label
        }
      }
      categoriseDimensionData: dimensionData {
        width
        height
      }
      style {
        backgroundImage {
          ...ResponsiveImageFragment
        }
        baseColour
        backgroundMatchActivity
        ...ComponentOuterStyleFragment
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
  ${ComponentOuterStyleFragment}
`;

const InteractiveRiveAnimationFragment = gql`
  fragment InteractiveRiveAnimationFragment on ActivitiesRiveAnimationComponent {
    id
    type
    language
    version
    properties {
      sourceUrl
      rive {
        key
        fileName
        link
        enableResetButton
        enableTextLabels
        textLabels {
          itemId
          text
          sort
        }
        enableButtons
        buttons {
          itemId
          text
          sort
        }
        buttonsPosition
      }
      dimensionData {
        width
        height
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      image {
        original
        fileName
        key
        alt
        data {
          designId
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

const InteractiveGifFragment = gql`
  fragment InteractiveGifFragment on ActivitiesGifComponent {
    id
    type
    language
    version
    properties {
      sourceUrl
      dimensionData {
        width
        height
      }
      image {
        original
        fileName
        key
        alt
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      printImage {
        original
        fileName
        key
        alt
      }
      printImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      style {
        ...ComponentOuterStyleFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ComponentOuterStyleFragment}
`;

export const InteractiveActivityFragment = gql`
  fragment InteractiveActivityFragment on ActivitiesActivityComponent {
    id
    type
    language
    version
    properties {
      activitySettings {
        differentiation
        duration
        checkPoint
        furtherEnrichment
      }
      subActivitiesLabelType
      aiRationale {
        curriculum
        cognitiveQuestionType
        context
      }
    }
    sectionId
    sort
    subActivities {
      ...InteractiveResponseFragment
      ...InteractiveMultipleChoiceFragment
      ...InteractiveEquationResponseFragment
      ...InteractiveFillInBlanksResponseFragment
      ...InteractiveDrawFragment
      ...InteractiveTableFragment
      ...InteractiveDiscussionFragment
      ...InteractiveInteractiveResponseFragment
      ...InteractiveInteractiveResponseCategoriseFragment
    }
  }
  ${InteractiveResponseFragment}
  ${InteractiveMultipleChoiceFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveFillInBlanksResponseFragment}
  ${InteractiveDrawFragment}
  ${InteractiveTableFragment}
  ${InteractiveDiscussionFragment}
  ${InteractiveInteractiveResponseSequenceFragment}
  ${InteractiveInteractiveResponseCategoriseFragment}
  ${ComponentOuterStyleFragment}
`;

export const InteractiveColumnsFragment = gql`
  fragment InteractiveColumnsFragment on ActivitiesColumnsComponent {
    id
    type
    language
    version
    sort
    sectionId
    columnProperties: properties {
      style {
        backgroundColour
        border
        baseColour
      }
    }
    columns {
      language
      version
      index
      columnId
      colspan
      alignment
      style {
        backgroundColour
        border
        baseColour
      }
      components {
        ...InteractiveInfoBoxFragment
        ...InteractiveVideoFragment
        ...InteractiveAudioFragment
        ...InteractiveResponseFragment
        ...InteractiveImageFragment
        ...InteractiveImageSphereFragment
        ...InteractiveLinkFragment
        ...InteractiveWebToolFragment
        ...InteractiveGuidedResearchFragment
        ...InteractiveEquationResponseFragment
        ...InteractiveSlideshowFragment
        ...InteractiveDocumentFragment
        ...InteractiveEbookFragment
        ...InteractiveEbookInteractiveFragment
        ...InteractiveInteractiveFragment
        ...InteractiveMultipleChoiceFragment
        ...InteractiveActivityFragment
        ...InteractiveVocabularyFragment
        ...InteractiveFillInBlanksResponseFragment
        ...InteractiveDrawFragment
        ...InteractiveTableFragment
        ...InteractiveDiscussionFragment
        ...InteractiveInteractiveResponseFragment
        ...InteractiveInteractiveResponseCategoriseFragment
        ...InteractiveRiveAnimationFragment
        ...InteractiveGifFragment
      }
    }
  }
  ${InteractiveInfoBoxFragment}
  ${InteractiveResponseFragment}
  ${InteractiveVideoFragment}
  ${InteractiveAudioFragment}
  ${InteractiveImageFragment}
  ${InteractiveImageSphereFragment}
  ${InteractiveLinkFragment}
  ${InteractiveWebToolFragment}
  ${InteractiveGuidedResearchFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveEbookFragment}
  ${InteractiveEbookInteractiveFragment}
  ${InteractiveInteractiveFragment}
  ${InteractiveSlideshowFragment}
  ${InteractiveDocumentFragment}
  ${InteractiveMultipleChoiceFragment}
  ${InteractiveActivityFragment}
  ${InteractiveVocabularyFragment}
  ${InteractiveFillInBlanksResponseFragment}
  ${InteractiveDrawFragment}
  ${InteractiveTableFragment}
  ${InteractiveDiscussionFragment}
  ${InteractiveInteractiveResponseSequenceFragment}
  ${InteractiveInteractiveResponseCategoriseFragment}
  ${InteractiveRiveAnimationFragment}
  ${InteractiveGifFragment}
`;

export const interactiveLessonSectionComponentFragment = gql`
  fragment InteractiveLessonSectionComponent on SectionComponent {
    ...InteractiveInfoBoxFragment
    ...InteractiveVideoFragment
    ...InteractiveAudioFragment
    ...InteractiveResponseFragment
    ...InteractiveImageFragment
    ...InteractiveImageSphereFragment
    ...InteractiveColumnsFragment
    ...InteractiveLinkFragment
    ...InteractiveWebToolFragment
    ...InteractiveGuidedResearchFragment
    ...InteractiveSlideshowFragment
    ...InteractiveEquationResponseFragment
    ...InteractiveDocumentFragment
    ...InteractiveEbookFragment
    ...InteractiveEbookInteractiveFragment
    ...InteractiveInteractiveFragment
    ...InteractiveMultipleChoiceFragment
    ...InteractiveActivityFragment
    ...InteractiveVocabularyFragment
    ...InteractiveFillInBlanksResponseFragment
    ...InteractiveDrawFragment
    ...InteractiveTableFragment
    ...InteractiveDiscussionFragment
    ...InteractiveInteractiveResponseFragment
    ...InteractiveInteractiveResponseCategoriseFragment
    ...InteractiveRiveAnimationFragment
    ...InteractiveGifFragment
  }
  ${ResponsiveImageFragment}
  ${InteractiveColumnsFragment}
  ${InteractiveInfoBoxFragment}
  ${InteractiveResponseFragment}
  ${InteractiveVideoFragment}
  ${InteractiveAudioFragment}
  ${InteractiveImageFragment}
  ${InteractiveImageSphereFragment}
  ${InteractiveLinkFragment}
  ${InteractiveWebToolFragment}
  ${InteractiveGuidedResearchFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveEbookFragment}
  ${InteractiveEbookInteractiveFragment}
  ${InteractiveInteractiveFragment}
  ${InteractiveSlideshowFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveDocumentFragment}
  ${InteractiveMultipleChoiceFragment}
  ${InteractiveActivityFragment}
  ${InteractiveVocabularyFragment}
  ${InteractiveFillInBlanksResponseFragment}
  ${InteractiveDrawFragment}
  ${InteractiveTableFragment}
  ${InteractiveDiscussionFragment}
  ${InteractiveInteractiveResponseSequenceFragment}
  ${InteractiveInteractiveResponseCategoriseFragment}
  ${InteractiveRiveAnimationFragment}
  ${InteractiveGifFragment}
`;

export const interactiveLessonSubSectionWithAnswersFragment = gql`
  fragment InteractiveLessonSubSectionWithAnswers on ActivitiesSectionWithAnswers {
    id
    activityId
    components {
      ...InteractiveLessonSectionComponent
    }
    sort
    parentSectionId
    pageType
    language
    version
    studentAnswers {
      ...AnswerFragment
    }
  }
  ${interactiveLessonSectionComponentFragment}
  ${AnswerFragment}
`;

export const interactiveLessonSubSectionFragment = gql`
  fragment InteractiveLessonSubSection on ActivitiesSection {
    id
    activityId
    components {
      ...InteractiveLessonSectionComponent
    }
    sort
    parentSectionId
    pageType
    language
    version
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const interactiveLessonSectionFragment = gql`
  fragment InteractiveLessonSectionFragment on ActivitiesSection {
    id
    activityId
    components {
      ...InteractiveLessonSectionComponent
    }
    subSections {
      ...InteractiveLessonSubSection
    }
    sort
    pageType
    language
    version
  }
  ${interactiveLessonSectionComponentFragment}
  ${interactiveLessonSubSectionFragment}
`;

export const interactiveLessonSectionOverviewFragment = gql`
  fragment InteractiveLessonSectionOverview on ActivitiesSectionOverview {
    id
    activityId
    differentiation
    checkPoint
    sort
    pageType
    hasResponseComponents
    subSectionsOverview {
      id
      differentiation
      checkPoint
      parentSectionId
      sort
      pageType
      activityId
      hasResponseComponents
    }
  }
`;

export const interactiveLessonFragment = gql`
  fragment LessonActivities on LessonActivities {
    id
    language
    version
    date
    outcomes {
      ...ActivityOutcomesFragment
    }
    focusQuestion {
      value
    }
    phenomena {
      value
    }
    landscapeImage {
      key
      fileName
      original
      alt
      data {
        flip
      }
      responsive {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    landscapeImageSourceUrl
    styles {
      fontSize
      ...InteractiveContentStyleBackground
    }
    lesson {
      authorId
      isAssigned
      assignedData {
        activitiesBookmarks {
          version
          sectionIds
        }
      }
      slug
      name
      years {
        name
      }
      unit {
        id
        name
      }
      subjects {
        id
        label
        name
        colour
      }
      landscapeImage {
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
      completion {
        ...Completion
      }
    }
    sectionsOverview {
      ...InteractiveLessonSectionOverview
    }
    status
    version
    hasUnpublishedChanges
    downloadUrl
    printPdfKey
    printPdfKeyPublicToken
    studentPrintables {
      downloadUrl
      previewUrl
    }
    assetKeys {
      print {
        language
        key
        publicToken
      }
      studentPrintables {
        language
        key
        publicToken
      }
    }
    assetDownloadUrls {
      language
      url
    }
    availableTranslations
  }
  ${interactiveLessonSectionOverviewFragment}
  ${interactiveContentStyleBackgroundFragment}
  ${ActivityOutcomesFragment}
  ${completionFragment}
`;

export const GET_DRAFT_LESSON_ACTIVITIES = gql`
  query GetDraftLessonActivities($input: GetLessonActivitiesInput!) {
    getDraftLessonActivities(input: $input) {
      id
      language
      date
      outcomes {
        ...ActivityOutcomesFragment
      }
      focusQuestion {
        value
      }
      phenomena {
        value
      }
      landscapeImage {
        key
        fileName
        original
        alt
        data {
          flip
        }
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
      landscapeImageSourceUrl
      styles {
        fontSize
        ...InteractiveContentStyleBackground
      }
      lesson {
        authorId
        isAssigned
        assignedData {
          activitiesBookmarks {
            version
            sectionIds
          }
        }
        completion {
          ...Completion
        }
        slug
        name
        years {
          name
        }
        unit {
          id
          name
        }
        subjects {
          id
          label
          name
          colour
        }
        landscapeImage {
          responsive {
            width
            height
            urls {
              url
              quality
            }
          }
        }
      }
      sectionsOverview {
        ...InteractiveLessonSectionOverview
      }
      status
      hasUnpublishedChanges
      downloadUrl
      printPdfKey
      printPdfKeyPublicToken
      studentPrintables {
        downloadUrl
        previewUrl
      }
      assetKeys {
        print {
          language
          key
          publicToken
        }
        studentPrintables {
          language
          key
          publicToken
        }
      }
      assetDownloadUrls {
        language
        url
      }
      availableTranslations
    }
  }
  ${interactiveLessonSectionOverviewFragment}
  ${interactiveContentStyleBackgroundFragment}
  ${ActivityOutcomesFragment}
  ${completionFragment}
`;

export const GET_ACTIVITIES_SECTIONS = gql`
  query GetActivitiesSections($input: GetActivitiesSectionsInput!) {
    getActivitiesSections(input: $input) {
      ...InteractiveLessonSectionFragment
    }
  }
  ${interactiveLessonSectionFragment}
`;

export const GET_ACTIVITIES_COMPONENT = gql`
  query GetActivitiesComponent($input: GetActivitiesComponentInput!) {
    getActivitiesComponent(input: $input) {
      ...InteractiveLessonSectionComponent
    }
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const BUILD_DRAFT_LESSON_ACTIVITIES = gql`
  mutation BuildDraftLessonActivities(
    $input: BuildDraftLessonActivitiesInput!
  ) {
    buildDraftLessonActivities(input: $input) {
      ...LessonActivities
    }
  }
  ${interactiveLessonFragment}
`;

export const INSERT_ACTIVITIES_SECTION = gql`
  mutation InsertActivitiesSection($input: InsertActivitiesSectionInput!) {
    insertActivitiesSection(input: $input) {
      ...InteractiveLessonSectionFragment
    }
  }
  ${interactiveLessonSectionFragment}
`;

export const UPDATE_ACTIVITIES_SECTION = gql`
  mutation UpdateActivitiesSection($input: UpdateActivitiesSectionInput!) {
    updateActivitiesSection(input: $input) {
      ...InteractiveLessonSectionFragment
    }
  }
  ${interactiveLessonSectionFragment}
`;

export const UPDATE_ACTIVITIES_COMPONENT = gql`
  mutation UpdateActivitiesComponent($input: UpdateActivitiesComponentInput!) {
    updateActivitiesComponent(input: $input) {
      ...InteractiveLessonSectionComponent
    }
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const DELETE_ACTIVITIES_COMPONENT = gql`
  mutation DeleteActivitiesComponent($input: DeleteActivitiesComponentInput!) {
    deleteActivitiesComponent(input: $input)
  }
`;

export const DELETE_ACTIVITIES_SECTION = gql`
  mutation DeleteActivitiesSection($input: DeleteActivitiesSectionInput!) {
    deleteActivitiesSection(input: $input)
  }
`;

export const GET_COMPONENT_TEMPLATES = gql`
  query GetComponentTemplates {
    getComponentTemplates {
      id
      data
      sort
      previews {
        format
        url
      }
      group
      name
      description
    }
  }
`;

export const USE_COMPONENT_TEMPLATE = gql`
  mutation UseComponentTemplate($input: UseComponentTemplateInput!) {
    useComponentTemplate(input: $input) {
      ...InteractiveLessonSectionComponent
    }
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const UPDATE_LESSON_ACTIVITIES_FONT_SIZE = gql`
  mutation UpdateLessonActivitiesFontSize($input: UpdateFontSizeInput!) {
    updateLessonActivitiesFontSize(input: $input) {
      id
      language
      version
      styles {
        fontSize
      }
    }
  }
`;

export const UPDATE_ACTIVITIES_FOCUS_QUESTION = gql`
  mutation UpdateActivitiesFocusQuestion(
    $input: UpdateLessonFocusQuestionInput!
  ) {
    updateActivitiesFocusQuestion(input: $input) {
      id
      language
      version
      focusQuestion {
        value
      }
    }
  }
`;

export const UPDATE_ACTIVITIES_PHENOMENA = gql`
  mutation UpdateActivitiesPhenomena($input: UpdateLessonPhenomenaInput!) {
    updateActivitiesPhenomena(input: $input) {
      id
      language
      version
      phenomena {
        value
      }
    }
  }
`;

export const GET_CURRENT_ACTIVITIES_EDITORS_SUBSCRIPTION = gql`
  subscription GetCurrentActivitiesEditors(
    $input: ActivitiesDraftEditingSubscriptionInput!
  ) {
    getCurrentActivitiesEditors(input: $input) {
      editorId
      firstName
      lastName
      emeji
      email
      displayName
      action
    }
  }
`;
